import { getComponentTypeData } from '../../business-logic/components/getComponentTypeData'
import { groupBy, difference, merge, flatten } from 'lodash'
import {
  USER_INPUT_FILTER_V1_ROLE,
  DETAILS_DATASET_ROLE,
} from '@wix/wix-data-client-common-standalone'
import * as connectionConfigHelper from '../../business-logic/connections/connectionConfigHelper'

const connectionId = connection =>
  JSON.stringify({
    controllerRef: connection.controllerRef,
    componentRef: connection.componentRef || connection.connectToRef, // TODO ??
    role: connection.role,
  })

const mergeConnections = connections => {
  const grouped = groupBy(connections, connection => connectionId(connection))
  return flatten(
    Object.values(grouped).map(
      connectionGroup =>
        connectionGroup.reduce((final, current) => ({
          ...final,
          ...current,
          config: merge({}, final.config, current.config),
        })),
      {},
    ),
  )
}

const getFilterIds = connections =>
  flatten(
    connections.map(connection =>
      connectionConfigHelper.getFilterBindingIds(connection.config),
    ),
  )

export const syncFilterConnections = async ({
  editorSdkProxy,
  expectedConnections,
  actualConnections,
}) => {
  const actualFilterIds = getFilterIds(actualConnections)
  const expectedFilterIds = getFilterIds(expectedConnections)
  const filterIdsToRemove = difference(actualFilterIds, expectedFilterIds)

  const actualConnectionsWithoutRemovedFilterIds = actualConnections.map(
    connection => ({
      ...connection,

      config: connectionConfigHelper.clearFilterBindingIds(
        connection.config,
        filterIdsToRemove,
      ),
    }),
  )

  const expectedAndEmptyConnections = mergeConnections(
    actualConnectionsWithoutRemovedFilterIds.concat(expectedConnections),
  )

  await Promise.all(
    expectedAndEmptyConnections.map(
      async ({ config, controllerRef, componentRef, role }) => {
        if (connectionConfigHelper.isEmpty(config)) {
          await editorSdkProxy.controllers.disconnect({
            controllerRef,
            connectToRef: componentRef,
            role,
          })
        } else {
          await editorSdkProxy.controllers.connect({
            controllerRef,
            connectToRef: componentRef,
            role,
            connectionConfig: config,
          })
        }
      },
    ),
  )
}

export const fetchFilterConnections = async ({
  editorSdkProxy,
  controllerRef,
}) => {
  const [componentConnections, controllerConnections] = await Promise.all([
    editorSdkProxy.controllers
      .getControllerConnections({ controllerRef })
      .then(cs =>
        Promise.all(
          cs
            .filter(
              ({ connection }) => connection.role === USER_INPUT_FILTER_V1_ROLE,
            )
            .map(async ({ connection, componentRef }) => {
              const { sdkType: componentType } = await getComponentTypeData({
                editorSdkProxy,
                componentRef,
              })
              return {
                ...connection,
                componentRef,
                componentType,
              }
            }),
        ),
      ),
    editorSdkProxy.controllers
      .listConnections({ componentRef: controllerRef })
      .then(cs =>
        cs
          .filter(controller => controller.role === DETAILS_DATASET_ROLE)
          .map(c => ({
            ...c,
            componentRef: controllerRef,
          })),
      ),
  ])

  return componentConnections.concat(controllerConnections)
}
